import { useRef, useState } from 'react';
import { ProtocolReview } from '../../../shared/types/protocol.ts';
import { ProtocolImageEntry } from '../../../shared/types/protocolImageEntry.ts';
import { useAutoSave } from '../../hooks/useAutoSave.ts';
import { isReviewRequired } from '../../services/protocol-review-required/isReviewRequired.ts';
import { Protocol, ProtocolDraft } from '../../types/sharedTypeImpl.ts';
import { reportError } from '../../utils/errorReporting.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { useProtocolStore, useProtocolStoreObj } from './protocolStoreProvider.tsx';


interface Props {
    pdfToUpload: Uint8Array | null;
    imagesToUpload: ProtocolImageEntry[];
    baseProtocol: Protocol | null;
    isViewMode: boolean;
    isFinalSavedRef: React.MutableRefObject<boolean>;
    onPdfUploaded: (data: Uint8Array) => void;
    onImageUploaded: (imageEntry: ProtocolImageEntry) => void;
    onFinalSaved: () => void;
    onReviewSaved: () => void;
}

export function useSaveProtocol({
    pdfToUpload,
    imagesToUpload,
    baseProtocol,
    isViewMode,
    isFinalSavedRef,
    onPdfUploaded,
    onImageUploaded,
    onFinalSaved,
    onReviewSaved,
}: Props) {
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [isFinalSaving, setIsFinalSaving] = useState(false);
    const isFinalSavePendingRef = useRef(false);
    const utils = trpc.useUtils();

    const protocolStore = useProtocolStoreObj();
    const protocol = useProtocolStore(state => state.protocol);
    const setProtocol = useProtocolStore(state => state.setProtocol);
    const isDirty = useProtocolStore(state => state.isDirty);
    const setIsDirty = useProtocolStore(state => state.setIsDirty);

    useAutoSave({
        isViewMode,
        isFinalized: isViewMode,
        isFinalSaving,
        isDirty: isDirty || !!pdfToUpload || imagesToUpload.length > 0,
        autoSaveCallback: async () => await handleDraftSave()
    });

    const trpcUtils = trpc.useUtils();
    const protocolSave = trpc.protocols.upsert.useMutation({
        onSuccess: () => trpcUtils.protocols.invalidate(),
    });

    const handleDraftSave = async () => {
        if (!protocol || isAutoSaving || isFinalSaving || isFinalSavedRef.current) return;
        setIsAutoSaving(true);
        console.log('Draft save started');
        await saveProtocol(protocol, pdfToUpload, imagesToUpload);

        setIsAutoSaving(false);
        console.log('Draft save ended');
        if (isFinalSavePendingRef.current) {
            isFinalSavePendingRef.current = false;
            handleFinalSave(protocol);
        }
    }

    const handleFinalSave = async (protocolDraft: ProtocolDraft) => {
        if (isFinalSaving || isFinalSavePendingRef.current || isFinalSavedRef.current) return;
        if (isAutoSaving) {
            isFinalSavePendingRef.current = true;
            return;
        }

        setIsFinalSaving(true);
        console.log('Final save started');

        const reviewRequired = isReviewRequired(baseProtocol, protocolDraft, pdfToUpload, imagesToUpload);
        const status = reviewRequired ? 'review' as const : 'approved' as const;
        const protocolFinal = {
            ...protocolDraft,
            status,
        }
        delete protocolFinal.requiresPlanReview;
        const savedProtocol = await saveProtocol(protocolFinal, pdfToUpload, imagesToUpload);

        setIsFinalSaving(false);
        console.log('Final save ended');
        if (savedProtocol) {
            isFinalSavedRef.current = true;
            onFinalSaved();
        }
    }

    const handleReviewSave = async (review: ProtocolReview) => {
        if (!protocol) return;
        try {
            setIsFinalSaving(true);

            const savedProtocol = await protocolSave.mutateAsync({
                protocol: {
                    ...protocol,
                    reviews: [...(protocol.reviews ?? []), review],
                    status: review.approved ? 'approved' : 'rejected',
                },
            });

            if (savedProtocol) {
                onReviewSaved();
            } else {
                alert('Failed to save');
            }
        } catch (error) {
            alert('Failed to save');
        } finally {
            setIsFinalSaving(false);
        }
    }

    const saveProtocol = async (
        protocolDraft: ProtocolDraft,
        pdfToUpload: Uint8Array | null,
        imagesToUpload: ProtocolImageEntry[],
    ): Promise<Protocol | null> => {
        if (!protocol) return null;
        try {
            setIsAutoSaving(true);
            const returnedProtocol = await protocolSave.mutateAsync({
                protocol: protocolDraft,
                inspectionImages: imagesToUpload,
                pdfFile: pdfToUpload ?? undefined,
            });

            handleProtocolSaved(returnedProtocol, pdfToUpload, imagesToUpload);
            return returnedProtocol;
        } catch (error) {
            alert('Failed to save');
            return null;
        } finally {
            setIsAutoSaving(false);
        }
    }

    const handleProtocolSaved = (
        returnedProtocol: Protocol,
        pdfToUpload: Uint8Array | null,
        imagesToUpload: ProtocolImageEntry[],
    ) => {
        const newProtocol = structuredClone(protocolStore.getState().protocol!);//Get current protocol from store, because user may be actively making edits
        newProtocol._id = returnedProtocol._id!;
        if (pdfToUpload) newProtocol.pdfFileName = returnedProtocol.pdfFileName;
        for (const imageToUpload of imagesToUpload) {
            const savedPosition = returnedProtocol.positions.find(it => it.id === imageToUpload.positionId);
            const newPosition = newProtocol.positions.find(it => it.id === imageToUpload.positionId);
            if (savedPosition && newPosition && savedPosition.imageFileName) {
                newPosition.imageFileName = savedPosition.imageFileName;
            } else {
                reportError('Failed to set position.imageFileName after uploading. Protocol id: ' + returnedProtocol._id);
            }
        }
        setProtocol(newProtocol);
        setIsDirty(false);

        if (pdfToUpload) {
            onPdfUploaded(pdfToUpload);
            utils.protocolFiles.getPdf.reset({ protocolId: returnedProtocol._id! });//Remove the pdf from cache
        }
        if (imagesToUpload.length > 0) {
            for (const imageEntry of imagesToUpload) {
                onImageUploaded(imageEntry);
            }
            utils.protocolFiles.getVisualInspectionImage.invalidate();
        }
    };


    return {
        isSaving: isAutoSaving || isFinalSaving,
        isFinalSaving,
        handleDraftSave,
        handleFinalSave,
        handleReviewSave,
    }
}
